import React, { useCallback, useMemo, useEffect, useState, useContext } from "react";
import { useDroppable } from "@dnd-kit/react";

import { Driver, DriverInfo, Delivery, DeliveryTracking, DriverTrackingUpdate, Dashboard, DropoutReason, WorkStatus } from "./model";
import { DriverShiftStatus } from "../driver_shift_status";
import { DeliveryStatus } from "../delivery_status";
import { DeliveryComponent, DeclineReasons, MaplinkNode } from "./delivery";
import { CurrentTimeContext } from "./current_time_context";
import { Intl, Temporal } from "@js-temporal/polyfill";

import { classNames } from "src/common_util";
import { adminDeliveriesPath, adminDriverPath, editAdminDriverShiftPath, adminChatsPath } from "src/routes";
import Modal from "src/admin/modal";
import { IDispatchClient, errorMessage, useDashboardConfig } from "./dispatch_api_client";
import { DropdownButton } from "../components/dropdown_button";
import { AddDriverControl } from "./add_driver_control";

interface DriverComponentProps {
  dashboard: Dashboard;
  driver: Driver;
};

interface InactiveDriverComponentProps {
  dashboard: Dashboard;
  driver: DriverInfo;
};

const createDriverNote = (apiClient: IDispatchClient, driverId: number, draft: string | undefined = undefined) => {
  Modal.show({
    content: `
      <div class="field">
        <div class="control">
          <label class="label">Note</label>
          <textarea class="textarea" data-form-validator-target="requiredField" placeholder="Note about what happened for the shift report - e.g. 'Driver was consistently running late all night'"></textarea>
        </div>
      </div>`,
    title: "New driver note",
    focus: "textarea",
    onShow: (content: HTMLElement) => {
      const textarea = content.querySelector("textarea") as HTMLTextAreaElement;
      if (draft && textarea) {
        textarea.defaultValue = draft;
        textarea.selectionStart = draft.length;
      }
    },
    onAccept: async (content: HTMLElement) => {
      await apiClient.createDriverNote(driverId, content.querySelector("textarea")!.value);
    },
  }).catch(() => undefined);
}

const RatingIcon = ({ rating }: { rating: number | null }) => {
  if (rating === null) {
    return <span title="Is new driver" className="icon is-new-driver">&#128118;</span>;
  }

  return <span className="is-inline-flex is-align-items-baseline" title={"Has " + rating.toString() + " star rating"}>
    <span className="icon has-text is-star-rating">
      &#11088;
    </span>

    <span className="is-size-7">
      {rating}
    </span>
  </span>;
}

const AlcoholDeliveryAllowedIcon = ({ alcoholDeliveryAllowed }: { alcoholDeliveryAllowed: boolean }) => {
  if (alcoholDeliveryAllowed) {
    return <span title="Can deliver alcohol" className="icon can-deliver-alcohol">&#127863;</span>;
  }
  return <></>;
}

const ExperienceIcon = ({ treatAsInexperienced }: { treatAsInexperienced: boolean }) => {
  if (treatAsInexperienced) {
    return <span title="Inexperienced" className="icon is-inexperienced">&#9888;&#65039;</span>;
  }
  return <></>;
}

const CurfewIcon = ({ tenPmCurfew }: { tenPmCurfew: boolean }) => {
  if (tenPmCurfew) {
    return <span title="Has driving curfew" className="icon has-driving-curfew">&#128345;</span>;
  }
  return <></>;
}

const VehicleIcon = ({ vehicleType }: { vehicleType: string }) => {
  switch (vehicleType) {
    case "Car":
      return <span title="Has car" className="icon is-car">&#128663;</span>;
    case "Scooter":
      return <span title="Has scooter" className="icon is-scooter">&#128757;</span>
    case "Motorbike":
      return <span title="Has motorbike" className="icon is-motorbike">&#127949;</span>
    case "E-bike":
      return <span title="Has e-bike" className="icon is-bicycle">&#128690;</span>;
    case "E-scooter":
      return <span title="Has e-scooter" className="icon is-escooter">&#128756;</span>;
  }
}

const dropoutReasonLabel = (workStatus: WorkStatus): string => {
  switch (workStatus) {
    case WorkStatus.Dropout:
      return "Dropout";
    case WorkStatus.NoComms:
      return "No comms";
    default:
      return "";
  }
}

const DropoutIcon = ({ workStatus }: { workStatus?: WorkStatus }) => {
  if (workStatus !== WorkStatus.Dropout && workStatus !== WorkStatus.NoComms) {
    return <></>;
  }
  return <span data-tooltip={dropoutReasonLabel(workStatus)} className="icon is-small">
      <i className="fas fa-plug"></i>
    </span>;
}

const InternalNote = ({ internalNote }: { internalNote: string }) => {
  if (internalNote === "") {
    return <></>;
  }
  return <div className="has-text-grey pb-1 px-2">
    <span className="has-text-grey">
      <span className="icon is-small">
        <i className="fas fa-exclamation-circle"></i>
      </span> {internalNote}
    </span>
  </div>;
}

const DriverLinkButtons = ({ driverId, shiftId }: { driverId: number; shiftId: number | null }) => {
  const currentTime = useContext(CurrentTimeContext);
  const date = currentTime.toLocaleDateString("en-NZ").replaceAll("/", "-");
  const deliveriesUrl = adminDeliveriesPath({
    "order_filter[driver_id]": driverId.toString(),
    "order_filter[start_date]": date,
    "order_filter[end_date]": date
  });

  return <div className="buttons has-addons m-1">
    <a className="button is-expanded is-small" href={adminDriverPath(driverId)} target="_blank" rel="noreferrer">
      <span>Driver</span>
    </a>

    <a className="button is-expanded is-small" href={deliveriesUrl} target="_blank" rel="noreferrer">
      <span>Deliveries</span>
    </a>

  {shiftId ?
    <a className="button is-small has-text-centered is-flex-grow-1" href={editAdminDriverShiftPath(shiftId)} target="_blank" rel="noreferrer">
      <span>Availability</span>
    </a> : <></>}
  </div>;
}

const CallDriverButton = ({ mobileNumber }: { mobileNumber: string }) =>
  <a href={`aircall://${mobileNumber.replaceAll(/\s+/g, "")}`} title={`Call ${mobileNumber}`} className="button is-small">
    <span className="icon">
      <i className="far fa-phone"></i>
    </span>
  </a>;

const AddNoteButton = ({ driverId }: { driverId: number }) => {
  const { apiClient } = useDashboardConfig();

  return <button onClick={() => createDriverNote(apiClient, driverId)} title="Add note" className="button is-small">
    <span className="icon is-small">
      <i className="far fa-file-pen"></i>
    </span>
  </button>;
}

const DriverComponent = ({ dashboard, driver }: DriverComponentProps) => {
  const { apiClient } = useDashboardConfig();
  const [expanded, setExpanded] = useState(false);
  const [deliveryTrackingList, setDeliveryTrackingList] = useState<DeliveryTracking[]>([]);
  const [removeError, setRemoveError] = useState<string>();

  const currentTime = useContext(CurrentTimeContext);

  /*
  Because the delivery tracking data is updated so much more frequently than everything else on
  the dash - 10-25Hz on a busy dash - we've invested the extra effort to have each driver
  component take care of its own delivery tracking state, using its own update subscription,
  rather than having it passed down from the top level. This massively reduces the amount of
  work React needs to do to figure out which components need re-rendering. In the actual
  dashboard view, we don't really need to bother initializing this state on mount, as the
  Driver components are long-lived, but in the chat case (and other potential future changes
  where Driver components might be shown and removed more frequently), it matters as we don't
  want admins to have to wait for the next update to come in before a newly-created component
  starts to show the tracking data.
  */
  useEffect(() => {
    const updateDriverTrackingState = (driverTrackingUpdate: DriverTrackingUpdate) => {
      if (driverTrackingUpdate.driverId == driver.id) {
        setDeliveryTrackingList(driverTrackingUpdate.deliveryTrackingList)
      }
    }

    apiClient.driverTrackingUpdated.addListener(updateDriverTrackingState);

    setDeliveryTrackingList(apiClient.deliveryTrackingForDriver(driver.id));

    return () => {
      apiClient.driverTrackingUpdated.removeListener(updateDriverTrackingState);
    }
  }, [driver.id, apiClient]);

  const toggleExpanded = () => {
    setExpanded(!expanded);
  }


  const { isDropTarget, ref } = useDroppable({
    id: `driver:${driver.id}`,
    data: {
      id: driver.id,
      type: "driver"
    }
  });

  const classes = isDropTarget ? "has-draggable-hover" : "";


  const handleRemoveDriver = useCallback(async (): Promise<void> => {
    setRemoveError(undefined);

    try {
      await apiClient.removeDriver(driver.id);
    } catch (e) {
      setRemoveError(errorMessage(e));
    }
  }, [driver.id, apiClient]);

  const handleDropoutDriver = useCallback(async (dropoutReason: DropoutReason): Promise<void> => {
    await apiClient.dropoutDriver(driver.id, dropoutReason);
  }, [driver.id, apiClient]);

  const handleUndoDropoutDriver = useCallback(async (): Promise<void> => {
    await apiClient.dropoutDriver(driver.id);
  }, [driver.id, apiClient]);



  const dropoffSequenceOrder = (a: Delivery, b: Delivery): number => {
    if (a.dropoffSequence != b.dropoffSequence) {
      return a.dropoffSequence - b.dropoffSequence;
    } else if (a.dropoffTimeIso8601 != b.dropoffTimeIso8601) {
      return a.dropoffTimeIso8601.valueOf() - b.dropoffTimeIso8601.valueOf();
    } else if (a.id < b.id) {
      return -1;
    } else if (a.id > b.id) {
      return 1;
    } else {
      return 0;
    }
  }

  const pickupSequenceOrder = (a: Delivery, b: Delivery): number => {
    if (a.pickupSequence != b.pickupSequence) {
      return a.pickupSequence - b.pickupSequence;
    } else if (a.pickupTimeIso8601 != b.pickupTimeIso8601) {
      return a.pickupTimeIso8601.valueOf() - b.pickupTimeIso8601.valueOf();
    } else if (a.id < b.id) {
      return -1;
    } else if (a.id > b.id) {
      return 1;
    } else {
      return 0;
    }
  }

  const relevantDeliveries = dashboard.deliveriesForDriver(driver.id);
  const deliveringNow = relevantDeliveries.every(delivery => delivery.pickedUpAt != null || delivery.deliveryStatus == DeliveryStatus.DriverDeclined);

  const deliveryComponentsGroup = (title: string | undefined, status: DeliveryStatus | DeliveryStatus[], sortFn: (a: Delivery, b: Delivery) => number) => {
    const statuses = Array.isArray(status) ? status : [status];
    const deliveries = relevantDeliveries.filter(delivery => statuses.includes(delivery.deliveryStatus));
    deliveries.sort(sortFn);

    const pickedUp = deliveries.every(delivery => delivery.pickedUpAt != null);
    const headerClasses = classNames({
      "category": true,
      "has-text-weight-medium": true,
      "py-1": true,
      "px-4": true,
      "has-background-grey-lighter": pickedUp,
      "has-background-grey-dark": !pickedUp,
      "has-text-white": !pickedUp,
    });

    return deliveries.length == 0 ? <></> : <>
      <div>
        <span className={headerClasses}><span>{title}<DeclineReasons deliveries={deliveries} /></span></span>
        {deliveryComponents(deliveries)}
      </div>
    </>;
  }

  const deliveryComponents = (deliveries: Delivery[]) => {
    return deliveries.map(delivery => {
      return (
        <DeliveryComponent
          key={delivery.id}
          dashboard={dashboard}
          defaultExpand={false}
          delivery={delivery}
          deliveryTracking={deliveryTrackingFor(delivery)}
          deliveringNow={deliveringNow}
        />
      )
    });
  }

  const deliveryTrackingFor = (delivery: Delivery): DeliveryTracking | undefined => {
    return deliveryTrackingList?.find((tracking: DeliveryTracking) => tracking.uid == delivery.id);
  }

  const renderActionButton = () => {
    if (dashboard.isDriverAssignedDeliveries(driver.id)) {
      return <></>;
    } else if (driver.status == DriverShiftStatus.Active) {
      const selectedGroupNumbers = dashboard.groupsSelectedForDriver(driver.id).map(group => group.groupNumber);
      const removeBlockedReason = selectedGroupNumbers.length > 0 ?
        `Unset ${driver.name} from group ${selectedGroupNumbers.join(", ")} first` : undefined;

      return (
        <div className="has-text-right is-flex-grow-1 ml-1">
          <span data-tooltip={removeBlockedReason}>
            <button className="button is-small is-warning" disabled={removeBlockedReason !== undefined} onClick={() => void handleRemoveDriver()}>Remove</button>
          </span>
          {removeError && <p className="help has-text-danger" role="alert">{removeError}</p>}
        </div>
      );
    } else if (![DriverShiftStatus.Onboarding, DriverShiftStatus.Offboarding].includes(driver.status)) {
      return (
        <AddDriverControl
          driverId={driver.id}
          driverName={driver.name}
          shiftTimes={dashboard.shiftTimes}
          requireExpectedEnd={driver.workStatus === undefined}
        />
      );
    }
  }

  const timeRange = (driver: Driver): React.JSX.Element => {
    if (driver.startTime && driver.endTime) {
      const formatter = new Intl.DateTimeFormat('en-NZ', { timeStyle: "short" });

      const start = formatter.format(driver.startTime);
      const end = formatter.format(driver.endTime);

      const currentZonedDateTime = Temporal.Instant.from(currentTime.toISOString()).toZonedDateTimeISO("Pacific/Auckland");
      const sinceStart = driver.startTime.since(currentZonedDateTime).total("minutes");
      const sinceEnd = driver.endTime.since(currentZonedDateTime).total("minutes");

      let expandedRange = <>{start} – {end}</>;

      // Driver starting within the next 30 minutes
      if (sinceStart > 0 && sinceStart <= 30) {
        expandedRange = <><span className="driver-start-time">{start}</span> – {end}</>;
      }

      // Driver finishing within the next 30 minutes, or already finished (< 0)
      else if (sinceEnd <= 30) {
        expandedRange = <>{start} – <span className="driver-end-time">{end}</span></>;
      }

      const hasUpcomingShifts = driver.upcomingShiftTimes.length > 0;
      const tooltip: { "data-tooltip"?: string } = {};

      if (hasUpcomingShifts) {
        const upcomingShiftTimes: string[] = driver.upcomingShiftTimes.map(shift => formatter.format(shift.startTime) + " – " + formatter.format(shift.endTime));
        tooltip["data-tooltip"] = upcomingShiftTimes.join("\r\n");
      }

      return <div className="has-text-grey pb-1 px-2">
        <span className="has-text-grey has-tooltip-arrow" {...tooltip}>
          <span className="icon is-small">
            <i className="far fa-sm fa-clock"></i>
          </span>
          {expandedRange}
          {hasUpcomingShifts ? " +" : ""}
        </span>
      </div>;
    } else {
      return <></>;
    }
  }

  const expandedDriverInfo = expanded ?
    <div className="p-2">
      <DriverLinkButtons driverId={driver.id} shiftId={driver.shiftId} />

      <div className="is-flex is-flex-wrap-wrap is-align-items-center is-gap-1 mx-1 mt-2">
        <CallDriverButton {...driver} />

        <a href={`${adminChatsPath()}/dispatcher?area[]=${dashboard.areaSlug}&focus=${driver.id}`} title="Chat" target="_blank" rel="noreferrer" className="button is-small">
          <span className="icon">
            <i className="far fa-message"></i>
          </span>
        </a>

        <AddNoteButton driverId={driver.id} />

        {!dashboard.isDriverAssignedDeliveries(driver.id) && driver.workStatus !== undefined && [WorkStatus.Pending, WorkStatus.Started, WorkStatus.Working].includes(driver.workStatus) ?
          <DropdownButton
            button={<span className="icon"><i className="fas fa-plug"></i></span>}
            buttonClasses={{ "is-small": true }}
            menuContent={
              <>
                <a className="dropdown-item" onClick={() => void handleDropoutDriver(DropoutReason.Dropout)}>Dropout</a>
                <a className="dropdown-item" onClick={() => void handleDropoutDriver(DropoutReason.NoComms)}>No comms</a>
              </>
            }
          /> : <></>}

        {!dashboard.isDriverAssignedDeliveries(driver.id) && (driver.workStatus === WorkStatus.Dropout || driver.workStatus === WorkStatus.NoComms) ?
          <DropdownButton
            button={<span className="icon"><i className="fas fa-plug"></i></span>}
            buttonClasses={{ "is-small": true }}
            menuContent={
              <a className="dropdown-item" onClick={() => void handleUndoDropoutDriver()}>Undo dropout</a>
            }
          /> : <></>}

        {renderActionButton()}
      </div>
    </div>
    : <></>;

  const expando = useMemo(() => {
    return expanded
      ? <span className="icon is-small mx-2 mt-1 driver-expando"><i className="fas fa-xs fa-chevron-up"></i></span>
      : <span className="icon is-small mx-2 mt-1 driver-expando"><i className="fas fa-xs fa-chevron-down"></i></span>;
  }, [expanded]);

  const waiting = useMemo(() => {
    if (driver.requestedDeliveriesAt === null || relevantDeliveries.length != 0) {
      return <></>;
    }

    const freeAt = new Date(driver.requestedDeliveriesAt);
    return <div><span className="category is-free-driver has-text-weight-medium py-1 px-3">Free at {freeAt.getHours() % 12 || 12}:{String(freeAt.getMinutes()).padStart(2, '0')}{freeAt.getHours() >= 12 ? "PM" : "AM"}, {driver.requestedDeliveriesLocation}</span></div>;
  }, [driver.requestedDeliveriesAt, driver.requestedDeliveriesLocation, relevantDeliveries.length]);

  const arrivalTimeOrHighNumber = (delivery: Delivery) => {
    return deliveryTrackingFor(delivery)?.liveArrivalEstimate?.getTime() ?? 9000000000000; // if liveArrivalEstimate returns nil, we can choose any arbitrary lastDelivery, and lastDeliveryArrivalTime will be set undefined and therefore we'll return <></> below
  }
  const collecting = relevantDeliveries.some((delivery) => delivery.pickedUpAt === null);
  const lastDelivery = relevantDeliveries.length > 0 ? relevantDeliveries.reduce((prev, delivery) => arrivalTimeOrHighNumber(delivery) > arrivalTimeOrHighNumber(prev) ? delivery : prev) : undefined;
  const lastDeliveryArrivalTime = lastDelivery ? deliveryTrackingFor(lastDelivery)?.liveArrivalEstimate : undefined;
  const busyUntil = lastDelivery && lastDeliveryArrivalTime ? <div>
    <span className={"category has-text-weight-medium py-1 px-3 " + (collecting ? "is-driver-free-at-collecting" : "is-driver-free-at")}>
      Last drop-off estimated {lastDeliveryArrivalTime?.toLocaleString("en", { timeStyle: "short" }).replace(' ', '').toLowerCase()}, {lastDelivery.suburb}
    </span>
  </div> : <></>;

  const dashNote = useMemo(() => {
    if (driver.dashNote === "") {
      return <></>;
    }
    return <div className="has-text-grey pb-1 px-2">
      <span className="has-text-grey">
        <span className="icon is-small">
          <i className="far fa-sm fa-sticky-note"></i>
        </span> {driver.dashNote}
      </span>
    </div>;
  }, [driver.dashNote]);

  const maplink = useMemo(() => {
    if (relevantDeliveries.length < 2) {
      return <></>;
    }
    return (
      <MaplinkNode deliveries={relevantDeliveries} />
    );
  }, [relevantDeliveries]);

  return (
    <div className="driver" id={`driver_${driver.id}`}>
      <div className={`card is-card-dashboard ${classes}`} ref={ref}>
        <header className="card-header" onClick={toggleExpanded}>
          <div className="card-header-title px-1 py-1 is-flex is-align-items-baseline">
            <div className="is-flex-grow-1 is-flex is-align-items-center">
              <div className="ml-1 mr-2">
                <RatingIcon {...driver} />
              </div>

              {driver.name}
            </div>

            <div className="mx-1 has-text-right">
              <DropoutIcon {...driver} />
              <AlcoholDeliveryAllowedIcon {...driver} />
              <ExperienceIcon {...driver} />
              <CurfewIcon {...driver} />
              <VehicleIcon {...driver} />
            </div>

            {maplink}
            {expando}
          </div>

          <div className="card-header-footer px-1 is-size-7">
            <InternalNote {...driver} />
          </div>

          <div className="card-header-footer px-1 is-size-7">
            {timeRange(driver)}
          </div>

          <div className="card-header-footer px-1 is-size-7">
            {dashNote}
          </div>
        </header>

        <div className="card-content p-0">
          <div className="card-header-expanded">
            {expandedDriverInfo}
          </div>
          {deliveryComponentsGroup('Declined by driver', [DeliveryStatus.DriverDeclined, DeliveryStatus.DropoffReassignedDeclined], pickupSequenceOrder)}
          {deliveryComponentsGroup('Arrived for return', DeliveryStatus.ReturnArrived, dropoffSequenceOrder)}
          {deliveryComponentsGroup('Return now', DeliveryStatus.ReturnNow, dropoffSequenceOrder)}
          {deliveryComponentsGroup('Return required', DeliveryStatus.ReturnLater, dropoffSequenceOrder)}
          {deliveryComponentsGroup('Arriving for dropoff', DeliveryStatus.DropoffArriving, dropoffSequenceOrder)}
          {deliveryComponentsGroup('Heading to dropoff', DeliveryStatus.DropoffNow, dropoffSequenceOrder)}
          {deliveryComponentsGroup('Dropoff later', DeliveryStatus.DropoffLater, dropoffSequenceOrder)}
          {deliveryComponentsGroup('Dropoff reassigned', DeliveryStatus.DropoffReassigned, dropoffSequenceOrder)}
          {deliveryComponentsGroup('Picked up already', DeliveryStatus.PickupComplete, dropoffSequenceOrder)}
          {deliveryComponentsGroup('Arrived for pickup', DeliveryStatus.PickupArrived, pickupSequenceOrder)}
          {deliveryComponentsGroup('Arriving for pickup', DeliveryStatus.PickupArriving, pickupSequenceOrder)}
          {deliveryComponentsGroup('Releasing for pickup', DeliveryStatus.PickupNearby, pickupSequenceOrder)}
          {deliveryComponentsGroup('Heading to pickup', DeliveryStatus.PickupNow, pickupSequenceOrder)}
          {deliveryComponentsGroup('Pickup later', DeliveryStatus.PickupLater, pickupSequenceOrder)}
          {deliveryComponentsGroup('Not accepted by driver', DeliveryStatus.Assigned, pickupSequenceOrder)}
          {waiting}
          {busyUntil}
        </div>
      </div>
    </div>
  );
}

const InactiveDriverComponent = ({ dashboard, driver }: InactiveDriverComponentProps) => {
  return (
    <div className="driver" id={`driver_${driver.id}`}>
      <div className="card is-card-dashboard">
        <header className="card-header">
          <div className="card-header-title px-1 py-1 is-flex is-align-items-baseline">
            <div className="is-flex-grow-1 is-flex is-align-items-center">
              <div className="ml-1 mr-2">
                <RatingIcon {...driver} />
              </div>

              {driver.name}
            </div>

            <div className="mx-1 has-text-right">
              <AlcoholDeliveryAllowedIcon {...driver} />
              <ExperienceIcon {...driver} />
              <CurfewIcon {...driver} />
              <VehicleIcon {...driver} />
            </div>
          </div>

          <div className="card-header-footer px-1 is-size-7">
            <InternalNote {...driver} />
          </div>
        </header>

        <div className="card-content p-0">
          <div className="card-header-expanded">
            <div className="p-2">
              <DriverLinkButtons driverId={driver.id} shiftId={null} />

              <div className="is-flex is-flex-wrap-wrap is-align-items-center is-gap-1 mx-1 mt-2">
                <CallDriverButton {...driver} />
                <AddNoteButton driverId={driver.id} />
                <AddDriverControl driverId={driver.id} driverName={driver.name} shiftTimes={dashboard.shiftTimes} requireExpectedEnd />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export { DriverComponent, InactiveDriverComponent, RatingIcon, AlcoholDeliveryAllowedIcon, ExperienceIcon, CurfewIcon, VehicleIcon, createDriverNote };
